import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { 
  FiPlay, FiClock, FiUsers, FiShield, FiCheck, FiStar, 
  FiDownload, FiArrowRight, FiMenu, FiX, FiPhone, FiMail,
  FiSmartphone, FiMonitor, FiTablet
} = FiIcons;

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 45,
    seconds: 30
  });

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const handleEnrollClick = () => {
    window.open('https://abdul.cc/ihtpay', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Sticky Navigation */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-lg shadow-xl z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">உ</span>
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  உடனடி சிகிச்சை
                </span>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('hero')} className="text-gray-700 hover:text-primary font-medium transition-colors">Home</button>
              <button onClick={() => scrollToSection('curriculum')} className="text-gray-700 hover:text-primary font-medium transition-colors">Curriculum</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-primary font-medium transition-colors">Reviews</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-primary font-medium transition-colors">FAQ</button>
              <button 
                onClick={handleEnrollClick}
                className="bg-gradient-to-r from-accent to-yellow-500 text-white px-8 py-3 rounded-full font-bold hover:shadow-lg transition-all transform hover:scale-105"
              >
                Enroll Now - ₹97
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2">
                <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="h-6 w-6 text-primary" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-lg border-t border-gray-100"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              <button onClick={() => scrollToSection('hero')} className="block px-3 py-3 text-gray-700 hover:bg-gray-50 rounded-lg w-full text-left">Home</button>
              <button onClick={() => scrollToSection('curriculum')} className="block px-3 py-3 text-gray-700 hover:bg-gray-50 rounded-lg w-full text-left">Curriculum</button>
              <button onClick={() => scrollToSection('testimonials')} className="block px-3 py-3 text-gray-700 hover:bg-gray-50 rounded-lg w-full text-left">Reviews</button>
              <button onClick={() => scrollToSection('faq')} className="block px-3 py-3 text-gray-700 hover:bg-gray-50 rounded-lg w-full text-left">FAQ</button>
              <button 
                onClick={handleEnrollClick}
                className="block w-full text-left px-3 py-3 bg-gradient-to-r from-accent to-yellow-500 text-white rounded-lg font-bold mt-2"
              >
                Enroll Now - ₹97
              </button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Trust Bar */}
      <div className="bg-gradient-to-r from-primary to-secondary text-white py-4 mt-16 shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 text-sm font-medium">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiUsers} className="w-5 h-5" />
              <span>12,000+ Learners</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiClock} className="w-5 h-5" />
              <span>7 Days Access</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiShield} className="w-5 h-5" />
              <span>100% Refund Guarantee</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section with Video */}
      <section id="hero" className="py-16 bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Content */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full mb-6">
                <span className="text-primary font-semibold text-sm">🔥 Limited Time Offer</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight max-w-5xl mx-auto">
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent font-tamil">
                  உடனடி சிகிச்சை முறையும்,
                </span>
                <br />
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent font-tamil">
                  நாடி பரிசோதனையும்
                </span>
                <br />
                <span className="text-gray-800 text-3xl md:text-4xl font-bold">
                  4 நாள் வீடியோ பயிற்சி!
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
                Learn at your own pace. <strong>7 days access</strong> to 4 powerful modules of Instant Healing — even if you're brand new.
              </p>
            </motion.div>
          </div>

          {/* Video and Content Grid */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Video Section */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-6 border border-gray-200">
                <div className="relative mb-6">
                  <div style={{padding: "56.25% 0 0 0", position: "relative"}}>
                    <iframe 
                      src="https://player.vimeo.com/video/932760900?h=ea338e2469&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" 
                      frameBorder="0" 
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
                      referrerPolicy="strict-origin-when-cross-origin" 
                      style={{position: "absolute", top: "0", left: "0", width: "100%", height: "100%", borderRadius: "16px"}}
                      title="Insta Heal Therapy 3 Days Masterclass"
                      className="shadow-lg"
                    ></iframe>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl pointer-events-none"></div>
                </div>
                
                <h3 className="text-2xl font-bold text-primary mb-3">Course Preview</h3>
                <p className="text-gray-600 mb-6">Get a glimpse of what you'll learn in this comprehensive video course</p>
                
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="bg-primary/10 text-primary px-3 py-1 rounded-full font-semibold">4 Modules</span>
                    <span className="bg-secondary/10 text-secondary px-3 py-1 rounded-full font-semibold">7 Days Access</span>
                  </div>
                  <span className="bg-accent/10 text-accent px-3 py-1 rounded-full font-semibold">Tamil Language</span>
                </div>
              </div>
            </motion.div>

            {/* Benefits and CTA */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              {/* Benefits */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  'Watch anytime, anywhere',
                  '7 days full access', 
                  'Heal family & friends naturally',
                  'Step-by-step video lessons'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white/50 rounded-lg p-3">
                    <div className="w-6 h-6 bg-gradient-to-r from-secondary to-teal-400 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Device Access */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 mb-8 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Access on All Your Devices</h3>
                <div className="flex items-center justify-center gap-8">
                  <div className="text-center">
                    <SafeIcon icon={FiSmartphone} className="w-8 h-8 text-secondary mx-auto mb-2" />
                    <span className="text-sm text-gray-600">iOS & Android</span>
                  </div>
                  <div className="text-center">
                    <SafeIcon icon={FiTablet} className="w-8 h-8 text-secondary mx-auto mb-2" />
                    <span className="text-sm text-gray-600">Tablet</span>
                  </div>
                  <div className="text-center">
                    <SafeIcon icon={FiMonitor} className="w-8 h-8 text-secondary mx-auto mb-2" />
                    <span className="text-sm text-gray-600">Browser</span>
                  </div>
                </div>
              </div>

              {/* Urgency Timer */}
              <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-2xl p-6 mb-8 shadow-xl">
                <p className="font-bold mb-4 text-center">⚡ Limited Time Offer Ends In:</p>
                <div className="flex justify-center gap-4">
                  <div className="text-center">
                    <div className="bg-white/20 backdrop-blur-sm text-white px-4 py-3 rounded-xl font-black text-2xl">
                      {String(timeLeft.hours).padStart(2, '0')}
                    </div>
                    <span className="text-xs font-medium mt-1 block">Hours</span>
                  </div>
                  <div className="text-center">
                    <div className="bg-white/20 backdrop-blur-sm text-white px-4 py-3 rounded-xl font-black text-2xl">
                      {String(timeLeft.minutes).padStart(2, '0')}
                    </div>
                    <span className="text-xs font-medium mt-1 block">Minutes</span>
                  </div>
                  <div className="text-center">
                    <div className="bg-white/20 backdrop-blur-sm text-white px-4 py-3 rounded-xl font-black text-2xl">
                      {String(timeLeft.seconds).padStart(2, '0')}
                    </div>
                    <span className="text-xs font-medium mt-1 block">Seconds</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={handleEnrollClick}
                className="w-full bg-gradient-to-r from-accent to-yellow-500 text-white px-10 py-5 rounded-2xl text-xl font-black hover:shadow-2xl transition-all transform hover:scale-105 shadow-xl"
              >
                Enroll Now — ₹97
                <SafeIcon icon={FiArrowRight} className="inline ml-3 w-6 h-6" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent font-tamil">
                இந்த பாடத்திட்டத்தை முடித்த பிறகு…
              </span>
              <br />
              <span className="text-gray-800">After this course you will…</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: FiCheck, title: 'Heal common pains quickly', desc: 'Master instant relief techniques for everyday ailments' },
              { icon: FiUsers, title: 'Use acupressure daily', desc: 'Apply fractal and acupressure methods confidently' },
              { icon: FiStar, title: 'Diagnose with pulse basics', desc: 'Learn fundamental Nadi Pariksha techniques' },
              { icon: FiShield, title: 'Build healing confidence', desc: 'Support your family and community naturally' },
              { icon: FiClock, title: 'Save on medications', desc: 'Reduce dependency on unnecessary treatments' },
              { icon: FiArrowRight, title: 'Start healer journey', desc: 'Begin your path to becoming a confident healer' }
            ].map((outcome, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-teal-50 p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-secondary to-teal-400 rounded-2xl flex items-center justify-center mb-6">
                  <SafeIcon icon={outcome.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-primary mb-3">{outcome.title}</h3>
                <p className="text-gray-600 leading-relaxed">{outcome.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section id="curriculum" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              What You'll Get Inside
            </h2>
            <p className="text-xl text-gray-600">4 comprehensive modules with 7 days full access</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                day: 'Module 1',
                title: 'Instant Heal Therapy Basics',
                desc: 'Foundation of acupressure, quick-relief points, lifestyle practices',
                lessons: ['Acupressure fundamentals', 'Quick relief techniques', 'Daily lifestyle integration', 'Safety guidelines']
              },
              {
                day: 'Module 2', 
                title: 'Fractal & NeuroScience Healing',
                desc: 'Practical healing demos, brain-based relief hacks',
                lessons: ['Fractal healing methods', 'Neuroscience applications', 'Practical demonstrations', 'Advanced techniques']
              },
              {
                day: 'Module 3',
                title: 'Pulse Diagnosis (Nadi Pariksha)',
                desc: 'Learn root-cause diagnosis through pulse, Yin-Yang, elements',
                lessons: ['Pulse reading basics', 'Yin-Yang principles', 'Elemental diagnosis', 'Root cause analysis']
              },
              {
                day: 'Module 4',
                title: '10x Energy Healer (Bonus)',
                desc: 'Chronic reversal principles, path to becoming a confident healer',
                lessons: ['Chronic condition reversal', 'Energy healing mastery', 'Healer mindset', 'Professional development']
              }
            ].map((module, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-200 hover:shadow-2xl transition-all"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="bg-gradient-to-r from-secondary to-teal-400 text-white px-4 py-2 rounded-full text-sm font-bold">
                    {module.day}
                  </span>
                  <span className="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                    <SafeIcon icon={FiPlay} className="w-3 h-3" />
                    7 Days Access
                  </span>
                </div>
                
                <h3 className="text-2xl font-bold text-primary mb-3">{module.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{module.desc}</p>
                
                <ul className="space-y-3">
                  {module.lessons.map((lesson, lessonIndex) => (
                    <li key={lessonIndex} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-gradient-to-r from-secondary to-teal-400 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-gray-700">{lesson}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-12">
              Transform Your Life Today
            </h2>
            
            <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl p-10 max-w-lg mx-auto border border-white/20">
              <div className="mb-8">
                <div className="text-gray-500 text-lg line-through mb-2">Original Price: ₹4,999</div>
                <div className="text-5xl font-black bg-gradient-to-r from-accent to-yellow-500 bg-clip-text text-transparent mb-2">₹97</div>
                <div className="text-gray-600 font-semibold">One-time payment</div>
              </div>

              <div className="space-y-4 mb-10">
                {[
                  '7 Days Full Access',
                  'iOS, Android & Browser',
                  'Instant Enrollment',
                  '100% Refund Guarantee (7 days)',
                  '4 Complete Video Modules',
                  'Tamil Language Course'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 font-medium text-left">{feature}</span>
                  </div>
                ))}
              </div>

              <button 
                onClick={handleEnrollClick}
                className="w-full bg-gradient-to-r from-accent to-yellow-500 text-white py-5 rounded-2xl text-xl font-black hover:shadow-2xl transition-all transform hover:scale-105 shadow-xl mb-6"
              >
                Enroll Now - ₹97
                <SafeIcon icon={FiArrowRight} className="inline ml-3 w-6 h-6" />
              </button>

              <p className="text-sm text-gray-500">
                🔒 Secure payment • ⚡ Instant access • 💯 7-day money-back guarantee
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              What Our Students Say
            </h2>
            <p className="text-xl text-gray-600">Thousands have already started their healing journey. You can too.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Priya Sharma',
                role: 'Software Engineer',
                text: 'இந்த பாடத்தின் மூலம் என் குடும்பத்தின் உடல்நலத்தை நான் கவனித்துக்கொள்ள முடிகிறது. மிகவும் பயனுள்ளது!',
                rating: 5
              },
              {
                name: 'Rajesh Kumar',
                role: 'Business Owner', 
                text: 'The pulse diagnosis techniques have helped me understand health issues before they become serious. Highly recommended!',
                rating: 5
              },
              {
                name: 'Meera Devi',
                role: 'Homemaker',
                text: 'வீட்டில் இருந்தே கற்றுக்கொள்ள முடிந்தது. குழந்தைகளின் சிறு உடல்நலப் பிரச்சனைகளை உடனே சரி செய்ய முடிகிறது.',
                rating: 5
              },
              {
                name: 'Dr. Anand',
                role: 'Physician',
                text: 'As a doctor, I found these traditional techniques complement modern medicine beautifully. Great course structure.',
                rating: 5
              },
              {
                name: 'Lakshmi Narayanan',
                role: 'Teacher',
                text: 'எளிமையான முறையில் கற்பிக்கப்பட்டுள்ளது. நான் என் மாணவர்களுக்கும் இதை பரிந்துரைக்கிறேன்.',
                rating: 5
              },
              {
                name: 'Arjun Patel',
                role: 'IT Professional',
                text: 'Work stress and back pain used to be constant problems. Now I can manage them naturally. Thank you!',
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">"{testimonial.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-primary text-lg">{testimonial.name}</p>
                  <p className="text-sm text-gray-500 font-medium">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trainer Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              Meet Your Mentor — Abdul Hakkim A
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759076648706-abdul%20teach%20pulse%20diagnosis.webp"
                  alt="Abdul Hakkim A teaching pulse diagnosis"
                  className="w-full rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-accent to-yellow-500 text-white p-6 rounded-2xl shadow-xl">
                  <div className="text-center">
                    <div className="text-2xl font-black">8000+</div>
                    <div className="text-sm font-semibold">Trusted Healers</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white/90 backdrop-blur-lg rounded-3xl p-8 shadow-xl border border-gray-200">
                <p className="text-lg text-gray-700 mb-6 font-tamil leading-relaxed">
                  பாரம்பரிய மருத்துவத்தில் 15+ ஆண்டுகள் அனுபவம் கொண்ட Abdul Hakkim A, ஆயிரக்கணக்கான மாணவர்களுக்கு உடனடி சிகிச்சை முறைகளை கற்பித்துள்ளார்.
                </p>
                
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  With over 15 years of experience in traditional healing methods, Abdul Hakkim A has trained thousands of students in the art of instant healing and pulse diagnosis. His unique approach combines ancient wisdom with modern understanding.
                </p>

                <div className="grid sm:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl">
                    <div className="text-3xl font-black text-secondary">8000+</div>
                    <div className="text-sm text-gray-600 font-semibold">Students Trained</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl">
                    <div className="text-3xl font-black text-secondary">12000+</div>
                    <div className="text-sm text-gray-600 font-semibold">Community Reach</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl">
                    <div className="text-3xl font-black text-secondary">15+</div>
                    <div className="text-sm text-gray-600 font-semibold">Years Experience</div>
                  </div>
                </div>

                <div className="space-y-4">
                  {[
                    'Certified Traditional Healer',
                    'Published Author on Healing Methods',
                    'Regular Speaker at Health Conferences',
                    'Founder of Classical Healing Institute'
                  ].map((credential, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-6 h-6 bg-gradient-to-r from-secondary to-teal-400 rounded-full flex items-center justify-center">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700 font-medium">{credential}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Format & Access */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-12">
              This is NOT a live class. Once you enroll, you get:
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: FiPlay,
                  title: 'Instant Access',
                  desc: 'Get immediate access to all 4 video modules after payment'
                },
                {
                  icon: FiClock,
                  title: '7 Days Access',
                  desc: 'Watch and rewatch the videos for a full week'
                },
                {
                  icon: FiUsers,
                  title: 'Learn at Your Pace',
                  desc: 'No fixed schedule - learn whenever convenient for you'
                },
                {
                  icon: FiPhone,
                  title: 'Support Available',
                  desc: 'Get your doubts cleared via WhatsApp and Email support'
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-secondary to-teal-400 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                q: 'When do I get access to the course?',
                a: 'You get immediate access to all course materials right after your payment is confirmed. No waiting time!'
              },
              {
                q: 'Do I need to attend live sessions?',
                a: 'No, this is a completely self-paced video course. All modules are pre-recorded and available 24/7 for 7 days.'
              },
              {
                q: 'How long do I have access?',
                a: 'You have 7 days full access to all course materials. Watch as many times as you want during this period.'
              },
              {
                q: 'Can I watch this on my mobile phone?',
                a: 'Absolutely! The course works on iOS, Android apps, tablets, and any web browser.'
              },
              {
                q: 'Will I get a certificate after completion?',
                a: 'Yes, you will receive a digital certificate of completion that you can download and print.'
              },
              {
                q: 'What if I\'m not satisfied with the course?',
                a: 'We offer a 100% money-back guarantee within 7 days of purchase. No questions asked!'
              },
              {
                q: 'Is the entire course in Tamil?',
                a: 'Yes, the entire course is conducted in Tamil for better understanding of traditional concepts.'
              },
              {
                q: 'What if I have questions during the course?',
                a: 'You can reach out to our support team via WhatsApp or email for any course-related queries.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white/90 backdrop-blur-lg rounded-2xl p-8 shadow-lg border border-gray-200"
              >
                <h3 className="text-xl font-bold text-primary mb-4">{faq.q}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-primary to-secondary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">உ</span>
                </div>
                <h3 className="text-2xl font-bold font-tamil">உடனடி சிகிச்சை</h3>
              </div>
              <p className="text-gray-200 leading-relaxed">
                Empowering people with traditional healing knowledge for a healthier life.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-6 text-lg">Quick Links</h4>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('hero')} className="block text-gray-200 hover:text-white transition-colors">Home</button>
                <button onClick={() => scrollToSection('curriculum')} className="block text-gray-200 hover:text-white transition-colors">Curriculum</button>
                <button onClick={() => scrollToSection('testimonials')} className="block text-gray-200 hover:text-white transition-colors">Testimonials</button>
                <button onClick={() => scrollToSection('faq')} className="block text-gray-200 hover:text-white transition-colors">FAQ</button>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-6 text-lg">Contact</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiMail} className="w-5 h-5" />
                  <span className="text-gray-200">support@yinguru.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                  <span className="text-gray-200">+91 9884899080</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/20 pt-8 text-center text-gray-200">
            <p className="mb-6 leading-relaxed">
              <strong>Medical Disclaimer:</strong> This course is for educational purposes only and is not intended to replace professional medical advice, diagnosis, or treatment. Always consult with qualified healthcare providers for medical concerns.
            </p>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6">
              <p className="text-sm leading-relaxed">
                This site is not a part of the Facebook website or Facebook Inc. Additionally, This site is NOT endorsed by Facebook in any way. FACEBOOK is a trademark of FACEBOOK, Inc. Please be advised that revenues and results shown are extraordinary and are not intended to serve as guarantees. In fact, as stipulated by law, we can not and do not make any guarantees about your ability to get results or earn any money with our ideas, information, tools or strategies. We don't know you and, besides, your results in life are up to you. Agreed? We just want to help you by giving great content, direction and strategies that worked well for us and our students and that we believe can move you forward. All of our terms, privacy policies and disclaimers for this program and website can be accessed via the link above. We feel transparency is important and we hold ourselves (and you) to a high standard of integrity. Thanks for stopping by. We hope this training and content brings you a lot of value.
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm mb-6">
              <a href="https://www.yinguru.com/privacypolicy" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="https://www.yinguru.com/termsofuse" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="https://www.yinguru.com/contactus" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Contact Us</a>
            </div>
            <p>&copy; 2025-26 Classical Healing - All Rights Reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-gray-200 shadow-2xl p-4 z-40 md:hidden">
        <button 
          onClick={handleEnrollClick}
          className="w-full bg-gradient-to-r from-accent to-yellow-500 text-white py-4 rounded-2xl font-black text-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
        >
          Join Now — ₹97 | Instant Access
          <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
        </button>
      </div>

      {/* Exit Intent Popup - Simple version for demo */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 hidden" id="exitPopup">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="bg-white rounded-3xl p-10 max-w-md w-full shadow-2xl">
            <h3 className="text-3xl font-black text-primary mb-6">Still thinking?</h3>
            <p className="text-gray-600 mb-8 leading-relaxed">Download our free course syllabus to see exactly what you'll learn!</p>
            <div className="flex gap-4">
              <button className="flex-1 bg-gradient-to-r from-secondary to-teal-400 text-white py-4 rounded-xl font-bold">
                <SafeIcon icon={FiDownload} className="inline mr-2 w-5 h-5" />
                Download Syllabus
              </button>
              <button className="px-4 py-4 text-gray-500 hover:text-gray-700">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Vimeo Player Script */}
      <script src="https://player.vimeo.com/api/player.js"></script>
    </div>
  );
}

export default App;